import type { Fact } from "../../common/fact-table";
import type { Character } from "./characters";

// How one code point is stored, in each of the three encodings that store it. A surrogate is the one code point none
// of them can hold on its own, so those rows are left empty rather than filled with the replacement character every
// encoder would quietly put there — `FactTable` drops a row with nothing in it, which is what makes that legible.
export function encodings(character: Character): Fact[] {
  const { code } = character;
  const alone = character.category === "Cs";

  return [
    { label: "Code point", value: `U+${hex(code, 4)}` },
    { label: "Decimal", value: String(code) },
    { label: "UTF-8", value: alone ? "" : utf8(code).map((byte) => hex(byte, 2)).join(" ") },
    { label: "UTF-16", value: utf16(code).map((unit) => hex(unit, 4)).join(" ") },
    { label: "UTF-32", value: hex(code, 8) },
    { label: "Binary", value: code.toString(2).padStart(8, "0") },
  ];
}

// Every spelling of the same code point, labelled by who reads it rather than by what the escape is called: `é`
// is nobody's idea of a name and "JSON, Java, C#" says which box it can be pasted into. Escaping a whole text is
// /string's, which is why nothing here takes one: these are the forms a character has, one character at a time.
export function escapes(character: Character, name: string): Fact[] {
  const { code } = character;
  const units = utf16(code);
  const alone = character.category === "Cs";

  return [
    { label: "JavaScript, Rust", value: `\\u{${hex(code, 2)}}` },
    // The pair for anything above the basic plane, which is the only form the three of them have ever accepted.
    { label: "JSON, Java, C#", value: units.map((unit) => `\\u${hex(unit, 4)}`).join("") },
    { label: "C, Python", value: `\\U${hex(code, 8)}` },
    // Python takes an alias here as well as a name, so a control is `\N{NULL}` where it has no name of its own.
    { label: "Python name", value: name === "" ? "" : `\\N{${name}}` },
    // Six digits exactly, which is the one spelling that needs no trailing space to say where the escape stops.
    { label: "CSS", value: `\\${hex(code, 6)}` },
    { label: "HTML, XML", value: `&#x${hex(code, 4)};` },
    { label: "HTML decimal", value: `&#${code};` },
    // Percent encoding is of bytes and never of code points, so this is the UTF-8 row spelled the way a URL wants it.
    { label: "URL", value: alone ? "" : utf8(code).map((byte) => `%${hex(byte, 2)}`).join("") },
  ];
}

export function utf8(code: number): number[] {
  return [...new TextEncoder().encode(String.fromCodePoint(code))];
}

export function utf16(code: number): number[] {
  if (code <= 0xFFFF) return [code];
  const above = code - 0x10000;
  return [0xD800 + (above >> 10), 0xDC00 + (above & 0x3FF)];
}

function hex(code: number, width: number): string {
  return code.toString(16).toUpperCase().padStart(width, "0");
}
